import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from "@angular/core";
import { NgIf } from "@angular/common";
import { IconDirective } from "@coreui/icons-angular";
import { BannerNewsService } from "@infra-adapters/services/banner-news.service";
import type { BannerPublishedView } from "@core-interfaces/services/banners-admin";

@Component({
  selector: "app-sticky-banner",
  templateUrl: "./sticky-banner.component.html",
  imports: [NgIf, IconDirective],
})
export class StickyBannerComponent implements OnInit, OnDestroy {
  private readonly news = inject(BannerNewsService);
  private rotateInterval: ReturnType<typeof setInterval> | null = null;

  readonly banners = this.news.activeBanners;
  readonly activeIndex = signal(0);
  readonly expanded = signal(false);

  readonly currentBanner = computed<BannerPublishedView | null>(() => {
    const list = this.banners();
    if (list.length === 0) return null;
    return list[this.activeIndex() % list.length] ?? null;
  });

  ngOnInit(): void {
    this.startRotation();
  }

  ngOnDestroy(): void {
    this.stopRotation();
  }

  private startRotation(): void {
    this.stopRotation();
    this.rotateInterval = setInterval(() => {
      const len = this.banners().length;
      if (len > 1) {
        this.activeIndex.update((i) => (i + 1) % len);
      }
    }, 10_000);
  }

  private stopRotation(): void {
    if (this.rotateInterval) {
      clearInterval(this.rotateInterval);
      this.rotateInterval = null;
    }
  }

  dismiss(): void {
    const b = this.currentBanner();
    if (b) {
      this.news.dismiss(b.id);
      this.activeIndex.set(0);
    }
  }

  onMouseEnter(): void {
    this.expanded.set(true);
    this.stopRotation();
  }

  onMouseLeave(): void {
    this.expanded.set(false);
    this.startRotation();
  }

  openLink(url: string | null): void {
    if (!url) return;
    let normalized = url.trim();
    if (normalized && !/^https?:\/\//i.test(normalized) && !/^mailto:/i.test(normalized) && !/^tel:/i.test(normalized)) {
      normalized = `https://${normalized}`;
    }
    window.open(normalized, "_blank", "noopener");
  }

  bannerBgColor(color: string): string {
    const map: Record<string, string> = {
      primary: "#3b82f6",
      info: "#38bdf8",
      success: "#10b981",
      warning: "#f59e0b",
      danger: "#ef4444",
    };
    return map[color] ?? "#3b82f6";
  }
}
